import { observer } from 'mobx-react-lite';

import { DetailItem } from 'components/details/DetailItem';
import { EditSelect, type EditSelectOptionList } from 'components/forms/EditSelect';
import { EditSchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import type { ReferenceType } from 'api/scheduling_windows/endpoints/types';

import type { EditableWindowLink } from './EditableWindowLink';

const LINK_TYPE_OPTIONS: EditSelectOptionList<ReferenceType> = [
  { key: 'open', value: 'open', label: 'Also open while that window is open' },
  { key: 'closed', value: 'closed', label: 'Closed while that window is open' },
];

interface Props {
  readonly link: EditableWindowLink;
}

/**
 * One linked window, as a single-line row: which window, and what it does to this
 * one. A link has only those two fields, so it does not earn a card.
 *
 * Its own `observer`: `DetailList` renders each item through a render-prop, which
 * runs outside its own reaction, so the field reads have to be tracked here or an
 * edit would not re-render the row.
 *
 * No cycle check. A link that would close a loop is refused by the database trigger
 * and by the route, and the dialog shows that message — the previous form
 * pre-filtered the options by walking a dependency graph built from every window in
 * the system, which duplicated the rule and needed the whole list loaded.
 */
export const WindowLinkRow = observer<Props>(({ link }) => (
  <DetailItem onRemove={link.delete} removeLabel="Remove link">
    <div className="flex-fill">
      <EditSchedulingWindowReference value={link.window} onChange={(v) => (link.window = v)} mandatory />
    </div>
    <div className="flex-fill">
      <EditSelect<ReferenceType> value={link.linkType} onChange={(v) => (link.linkType = v)} options={LINK_TYPE_OPTIONS} mandatory />
    </div>
  </DetailItem>
));
